import React, { useEffect, useState } from "react";
import SyllabusListing from "./SyllabusListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/SyllabusSlice";
import AddSyllabusWrapper from "../Add/AddSyllabusWrapper";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import EditSyllabusWrapper from "../Edit/EditSyllabusWrapper";
import {
  useChangeStatusSyllabusByIdMutation,
  useDeleteSyllabusByIdMutation,
  useGetAllSyllabusDataQuery,
} from "src/services/SyllabusService";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const SyllabusListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    isOpenAddDialog,
    isOpenEditDialog,
    searchValue,
    isTableLoading,
  } = useSelector((state: RootState) => state.syllabus);
  const [selectedSyllabusId, setSelectedSyllabusId] = useState<string>();
  const [deleteSyllabus] = useDeleteSyllabusByIdMutation();
  const [changeStatusSyllabus] = useChangeStatusSyllabusByIdMutation();
  const { data, isLoading, isFetching } = useGetAllSyllabusDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["syllabusName"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteSyllabus = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Syllabus ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteSyllabus(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusSyllabus = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Syllabus ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusSyllabus({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const moduleName = "SYLLABUS";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    {
      field: "syllabusName",
      headerName: "Syllabus Name",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "order",
      headerName: "order",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "center",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = [];

        if (canEdit) {
          options.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedSyllabusId(row?._id);
            },
          });
        }

        if (canActive) {
          options.push({
            label: "Active/Deactive",
            onClick: () => {
              handleChangeStatusSyllabus(row?._id);
            },
          });
        }

        if (canDelete) {
          options.push({
            label: "Delete",
            onClick: () => {
              handleDeleteSyllabus(row?._id);
            },
          });
        }

        return <ATMMenu options={options} />;
      },
    },
  ];

  return (
    <>
      <div>
        <SyllabusListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddSyllabusWrapper
          onClose={() => dispatch(setIsOpenAddDialog(false))}
        />
      ) : null}
      {isOpenEditDialog ? (
        <EditSyllabusWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedSyllabusId={selectedSyllabusId || ""}
        />
      ) : null}
    </>
  );
};

export default SyllabusListingWrapper;
